import { Can } from "@/casl-provider";
import { useOpenPositionBulkDeleteMutation } from "@/common/api/open-position/use-open-position-bulk-delete.mutation";
import { useOpenPositionFullSyncMutation } from "@/common/api/open-position/use-open-position-full-sync.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { useUrlEnumState } from "@/common/hooks/use-url-enum-state";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { MultiToggle } from "@/components/multi-toggle/multi-toggle";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CommandButton } from "@/components/command-palette/command-button";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { FiaTrackingEvent, trackFiaEvent } from "@/modules/fia/fia.tracking";
import { Building2Icon, ReceiptEuroIcon, RefreshCwIcon, Trash2Icon, UsersIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BranchAggregateTable } from "./aggregate/branch-aggregate-table";
import { CustomerAggregateTable } from "./aggregate/customer-aggregate-table";
import { OpenPositionTable } from "./positions/open-position-table";

enum OpenPositionViewMode {
  Positions = "positions",
  ByCustomer = "byCustomer",
  ByBranch = "byBranch",
}

export function OpenPositionView() {
  const { mutateAsync: triggerFullSync, isPending: isFullSyncPending } = useOpenPositionFullSyncMutation();
  const { t } = useTranslation(["fia", "common"]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteRevision, setDeleteRevision] = useState(0);
  const { mutateAsync: deleteOpenPositions, isPending: isDeletePending } = useOpenPositionBulkDeleteMutation();
  const [viewMode, setViewMode] = useUrlEnumState<OpenPositionViewMode>(
    "mode",
    Object.values(OpenPositionViewMode),
    OpenPositionViewMode.Positions,
  );

  const handleViewModeChange = (mode: OpenPositionViewMode) => {
    if (mode !== viewMode) {
      trackFiaEvent(FiaTrackingEvent.OpenPositionViewModeSelected, { mode });
    }
    setViewMode(mode);
  };

  const handleFullSync = async (): Promise<void> => {
    await toastMutationStatus({
      mutationFn: triggerFullSync,
      successMessage: t("fia:OpenPositionFullSyncStarted"),
      errorMessage: t("fia:OpenPositionFullSyncError"),
    });
  };

  const handleDeleteAll = async () => {
    await toastMutationStatus({
      mutationFn: deleteOpenPositions,
      errorMessage: t("fia:DeleteAllOpenPositionsError"),
      onSuccess: ({ deletedOpenPositionCount }) => {
        setDeleteRevision((revision) => revision + 1);
        setIsDeleteDialogOpen(false);
        toast.success(t("fia:OpenPositionsDeleted", { count: deletedOpenPositionCount }));
      },
    });
  };

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Table}>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <MultiToggle
          aria-label={t("fia:ViewModeLabel")}
          value={viewMode}
          onChange={handleViewModeChange}
          options={[
            { label: t("fia:ViewAllPositions"), icon: ReceiptEuroIcon, value: OpenPositionViewMode.Positions },
            { label: t("fia:ViewByCustomer"), icon: UsersIcon, value: OpenPositionViewMode.ByCustomer },
            { label: t("fia:ViewByBranch"), icon: Building2Icon, value: OpenPositionViewMode.ByBranch },
          ]}
        />
        <Can I="manage" a="OpenPosition">
          <CommandButton
            commandId="fia-open-position-full-sync"
            label={t("fia:OpenPositionFullSync")}
            icon={<RefreshCwIcon />}
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            loading={isFullSyncPending}
            onClick={() => void handleFullSync()}
          />
          <CommandButton
            commandId="fia-open-position-delete-all"
            label={t("fia:DeleteAllOpenPositions")}
            icon={<Trash2Icon />}
            colorVariant={ButtonColorVariant.Error}
            loading={isDeletePending}
            onClick={() => setIsDeleteDialogOpen(true)}
          />
        </Can>
      </div>

      <div key={deleteRevision} className="flex min-h-0 flex-1 flex-col">
        {viewMode === OpenPositionViewMode.Positions && <OpenPositionTable />}
        {viewMode === OpenPositionViewMode.ByCustomer && <CustomerAggregateTable />}
        {viewMode === OpenPositionViewMode.ByBranch && <BranchAggregateTable />}
      </div>
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          if (!isDeletePending) setIsDeleteDialogOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("fia:DeleteAllOpenPositions")}</AlertDialogTitle>
            <AlertDialogDescription>{t("fia:DeleteAllOpenPositionsConfirmation")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletePending}>{t("common:Cancel")}</AlertDialogCancel>
            <Button
              colorVariant={ButtonColorVariant.Error}
              disabled={isDeletePending}
              onClick={() => void handleDeleteAll()}
            >
              {isDeletePending && <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />}
              {t("fia:DeleteAllOpenPositions")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CenteredLayout>
  );
}
